import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import { toast } from "react-toastify";
import {
  PageContainer, PremiumCard, FormSection, Field, SelectField, Button,
  LoadingState, EmptyState,
} from "components/ui";

const AddTenant = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useCurrentUser();
  const tenant = user?.tenant;

  const [form, setForm] = useState({
    name: "", slug: "", logoUrl: "",
    contactEmail: "", contactPhone: "",
    address: "", taxId: "", commercialRegNumber: "",
    website: "", defaultCurrency: "TND", defaultLanguage: "fr",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const change = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Organization name is required.";
    if (!form.slug.trim()) errs.slug = "A unique slug is required.";
    if (!form.contactEmail.trim()) errs.contactEmail = "Contact email is required.";
    else if (!/\S+@\S+\.\S+/.test(form.contactEmail)) errs.contactEmail = "Invalid email address.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axiosPrivate.post("/api/tenants", { ...form, active: false });
      toast.success("Organization created successfully!");
      navigate("/tenant/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create organization.");
    } finally {
      setSubmitting(false);
    }
  };

  if (userLoading) {
    return (
      <PageContainer narrow>
        <PremiumCard><LoadingState /></PremiumCard>
      </PageContainer>
    );
  }
  if (tenant) {
    return (
      <PageContainer narrow>
        <PremiumCard>
          <EmptyState
            title="Organization already exists"
            description="You have already created an organization. Each account can only have one."
            action={<Button variant="primary" onClick={() => navigate("/tenant/dashboard")}>Go to dashboard</Button>}
          />
        </PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer narrow>
      <PremiumCard title="Create your organization" subtitle="Set up your tenant profile before subscribing to a plan">
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: 24 }}>
            <FormSection title="Identity" columns={2}>
              <Field label="Organization name" required value={form.name} onChange={change("name")} error={errors.name} />
              <Field label="Slug" required value={form.slug} onChange={change("slug")} error={errors.slug} placeholder="my-company" />
            </FormSection>

            <FormSection title="Contact" columns={2}>
              <Field label="Contact email" required type="email" value={form.contactEmail} onChange={change("contactEmail")} error={errors.contactEmail} />
              <Field label="Phone" value={form.contactPhone} onChange={change("contactPhone")} />
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Address" value={form.address} onChange={change("address")} />
              </div>
            </FormSection>

            <FormSection title="Legal" columns={2}>
              <Field label="Tax ID (VAT)" value={form.taxId} onChange={change("taxId")} />
              <Field label="Commercial reg. #" value={form.commercialRegNumber} onChange={change("commercialRegNumber")} />
            </FormSection>

            <FormSection title="Branding" columns={2}>
              <Field label="Website" type="url" placeholder="https://www.example.com" value={form.website} onChange={change("website")} />
              <Field label="Logo URL" type="url" placeholder="https://…/logo.png" value={form.logoUrl} onChange={change("logoUrl")} />
            </FormSection>

            <FormSection title="Preferences" columns={2}>
              <SelectField label="Default currency" value={form.defaultCurrency} onChange={change("defaultCurrency")}>
                <option value="TND">TND</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </SelectField>
              <SelectField label="Default language" value={form.defaultLanguage} onChange={change("defaultLanguage")}>
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </SelectField>
            </FormSection>
          </div>

          <div className="form-actionbar">
            <Button type="button" variant="secondary" onClick={() => navigate("/tenant/dashboard")}>Cancel</Button>
            <Button type="submit" variant="primary" loading={submitting}>Create organization</Button>
          </div>
        </form>
      </PremiumCard>
    </PageContainer>
  );
};

export default AddTenant;
